<template>
  <div class="confirm-buy-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-check-circle me-2 text-primary"></i>
            <strong>Confirm Purchase</strong>
          </h4>
          <small class="text-muted">Confirm vehicle purchase details and manage images</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading purchase details...</p>
      </div>
    </div>

    <!-- Confirm Buy Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-shopping-cart me-2 text-primary"></i>
              <strong>Stock To Buy Information</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Basic Information Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-info-circle me-2 text-primary"></i>
                    <strong>Basic Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Stock No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.stockNo"
                    :readonly="isReadOnly"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-hashtag me-1"></i>
                    Stock reference number
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Description</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.description"
                    :readonly="isReadOnly"
                    rows="5"
                    placeholder="Enter vehicle description and details"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-align-left me-1"></i>
                    Detailed description of the vehicle
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Color</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.color"
                    :readonly="isReadOnly"
                    placeholder="Enter vehicle color"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-palette me-1"></i>
                    Vehicle exterior color
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Company & Supplier Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-building me-2 text-primary"></i>
                    <strong>Company & Supplier Details</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>AP Company</strong>
                </CCol>
                <CCol sm="9">
                  <v-select
                    v-model="selectedSubCompany"
                    :options="subCompanyItems"
                    :disabled="isReadOnly"
                    label="name"
                    placeholder="Select AP Company"
                    class="form-control-lg"
                    style="width: 100%; padding: 0"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-building me-1"></i>
                    Approved Permit company
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Supplier</strong>
                </CCol>
                <CCol sm="9">
                  <v-select
                    v-model="selectedSupplier"
                    :options="supplierItems"
                    :disabled="isReadOnly"
                    label="name"
                    placeholder="Select Supplier"
                    class="form-control-lg"
                    style="width: 100%; padding: 0"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-truck me-1"></i>
                    Vehicle supplier
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Pricing Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-dollar-sign me-2 text-primary"></i>
                    <strong>Pricing Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Supplier Currency</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.supplierCurrency"
                    placeholder="e.g., JPY, USD"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-yen-sign me-1"></i>
                    Currency code from supplier
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Price (Foreign Currency)</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model.number="obj.vehiclePriceSupplierCurrency"
                    :prepend="obj.supplierCurrency || 'Foreign'"
                    type="number"
                    step="0.01"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-globe me-1"></i>
                    Vehicle price in supplier's currency
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Price (Local Currency)</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model.number="obj.vehiclePriceLocalCurrency"
                    prepend="RM"
                    type="number"
                    step="0.01"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-money-bill me-1"></i>
                    Vehicle price in local currency
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Body Price</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model.number="obj.bodyPriceLocalCurrency"
                    prepend="RM"
                    type="number"
                    step="0.01"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-car me-1"></i>
                    Vehicle body price in local currency
                  </small>
                </CCol>
              </CRow>

              <!-- Image Management Section (only when editing) -->
              <template v-if="obj.id">
                <!-- Divider -->
                <hr class="my-4" />

                <CRow class="mb-3">
                  <CCol>
                    <h6 class="section-header">
                      <i class="fas fa-images me-2 text-primary"></i>
                      <strong>Vehicle Images</strong>
                    </h6>
                  </CCol>
                </CRow>

                <CRow class="mb-3">
                  <CCol tag="label" sm="3" class="col-form-label">
                    <strong>Uploaded Images</strong>
                  </CCol>
                  <CCol sm="9">
                    <CRow>
                      <CCol
                        v-for="(item, index) in sortedVehiclePhotos"
                        :key="item.id"
                        xs="12"
                        sm="6"
                        md="4"
                        lg="3"
                        xl="2"
                        class="d-flex justify-content-center position-relative mb-4"
                      >
                        <div class="image-container">
                          <CImg
                            :src="getImageUrl(item)"
                            thumbnail
                            class="img-fluid"
                            style="max-width: 100%; cursor: pointer"
                          />
                          <!-- Buttons overlay -->
                          <div class="button-overlay">
                            <div class="button-row">
                              <CButton
                                size="sm"
                                color="primary"
                                class="move-button"
                                @click="moveImageUp(index)"
                                :disabled="index === 0"
                                title="Move up"
                              >
                                ▲
                              </CButton>
                              <CButton
                                size="sm"
                                color="primary"
                                class="move-button"
                                @click="moveImageDown(index)"
                                :disabled="index === sortedVehiclePhotos.length - 1"
                                title="Move down"
                              >
                                ▼
                              </CButton>
                            </div>
                            <div class="button-row">
                              <CButton
                                size="sm"
                                color="danger"
                                class="move-button"
                                @click="deleteImage(item.id)"
                                title="Delete image"
                              >
                                🗑
                              </CButton>
                              <CButton
                                size="sm"
                                color="success"
                                class="move-button"
                                @click="setDefaultImage(item)"
                                :disabled="isDefaultImage(item)"
                                title="Set as default"
                              >
                                <i class="fas fa-star"></i>
                              </CButton>
                            </div>
                            <CButton
                              size="sm"
                              color="info"
                              class="move-button"
                              @click="maximizeImage(item)"
                              title="View full size"
                            >
                              🔍
                            </CButton>
                          </div>

                          <span
                            v-if="isDefaultImage(item)"
                            class="default-badge"
                          >
                            <i class="fas fa-star me-1"></i>
                            Default
                          </span>
                        </div>
                      </CCol>
                    </CRow>
                    <small class="form-text text-muted">
                      <i class="fas fa-info-circle me-1"></i>
                      Hover over images to reorder, delete, or set as default
                    </small>
                  </CCol>
                </CRow>

                <CRow class="mb-3">
                  <CCol tag="label" sm="3" class="col-form-label">
                    <strong>Upload New Images</strong>
                  </CCol>
                  <CCol sm="9">
                    <WidgetsUploadImage @uploaded="media_uploaded" />
                    <small class="form-text text-muted">
                      <i class="fas fa-cloud-upload-alt me-1"></i>
                      Upload additional vehicle images
                    </small>
                  </CCol>
                </CRow>
              </template>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  v-if="canSubmit"
                  color="primary"
                  :disabled="submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <small class="text-muted" v-if="obj.id">
                <i class="fas fa-info-circle me-1"></i>
                Images can be managed after saving
              </small>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Image Maximizer Modal -->
    <CModal
      :show.sync="isMaximizeImageModal"
      size="lg"
      @close="isMaximizeImageModal = false"
    >
      <template #header>
        <h5>
          <i class="fas fa-expand me-2"></i>
          Maximized Image
        </h5>
      </template>
      <CImg :src="currentImageUrl" class="maximized-image" />
      <template #footer>
        <CButton
          color="secondary"
          @click="isMaximizeImageModal = false"
        >
          <i class="fas fa-times me-2"></i>
          Close
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import StockToBuyApi from "../../lib/stockToBuyApi";
import SubCompanyApi from "../../lib/subCompanyApi";
import SupplierApi from "../../lib/supplierApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage";
import Helper from "../../lib/helper";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const helper = new Helper();

export default {
  name: "ConfirmBuy",
  components: {
    vSelect,
    WidgetsUploadImage,
  },
  data() {
    return {
      isMaximizeImageModal: false,
      currentImageUrl: "",
      supplierItems: [],
      selectedSupplier: null,
      infoList: [],
      api: new StockToBuyApi(),
      subCompanyApi: new SubCompanyApi(),
      supplierApi: new SupplierApi(),
      obj: {
        stockNo: "",
        description: "",
        color: "",
        supplierCurrency: "",
        vehiclePriceSupplierCurrency: 0,
        vehiclePriceLocalCurrency: 0,
        bodyPriceLocalCurrency: 0,
        vehiclePhotoList: [],
        defaultImageUrl: "",
      },
      loading: false,
      submitting: false,
      subCompanyItems: [],
      selectedSubCompany: null,
    };
  },
  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/ConfirmBuyList');
      this.toast('Access Denied', 'You do not have permission to add stock to buy', 'danger');
      return;
    }

    this.refreshSubCompany();
    this.refreshSupplier();
    this.resetObj();
  },
  computed: {
    sortedVehiclePhotos() {
      if (!this.obj.vehiclePhotoList) return [];
      return [...this.obj.vehiclePhotoList].sort(
        (a, b) => a.position - b.position
      );
    },
    canView() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanView');
    },
    canAdd() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanUpdate');
    },
    isReadOnly() {
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    },
  },
  methods: {
    media_uploaded(data) {
      this.api
        .updateVehicleImages(this.obj.id, data.uploadedFiles)
        .then((response) => {
          this.toast("Success", "Images uploaded successfully", "success");
          this.resetObj();
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    maximizeImage(item) {
      this.currentImageUrl = this.getImageUrl(item);
      this.isMaximizeImageModal = true;
    },

    moveImageUp(index) {
      if (index > 0) {
        const currentImage = this.obj.vehiclePhotoList[index];
        const previousImage = this.obj.vehiclePhotoList[index - 1];
        currentImage.position--;
        previousImage.position++;
        this.sortAndSaveImages();
      }
    },

    moveImageDown(index) {
      if (index < this.obj.vehiclePhotoList.length - 1) {
        const currentImage = this.obj.vehiclePhotoList[index];
        const nextImage = this.obj.vehiclePhotoList[index + 1];
        currentImage.position++;
        nextImage.position--;
        this.sortAndSaveImages();
      }
    },

    sortAndSaveImages() {
      this.obj.vehiclePhotoList.sort((a, b) => a.position - b.position);
      this.api
        .updateImagePosition(this.obj)
        .then((response) => {
          this.toast("Success", "Image position updated", "success");
          this.resetObj();
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    isDefaultImage(item) {
      return (
        "/Documents/File/" + item.documentId === this.obj.defaultImageUrl
      );
    },

    deleteImage(imageId) {
      if (confirm("Are you sure you want to delete this image?")) {
        this.api
          .deleteVehicleImage(imageId)
          .then((response) => {
            this.toast("Success", "Image deleted successfully", "success");
            this.resetObj();
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    setDefaultImage(item) {
      this.obj.defaultImageUrl = "/Documents/File/" + item.documentId;
      this.api
        .updateDefaultImages(this.obj)
        .then((response) => {
          this.toast("Success", "Default image set successfully", "success");
          this.resetObj();
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    getImageUrl(item) {
      return apiUrl + "Documents/File/" + item.documentId;
    },

    refreshSupplier() {
      this.supplierApi
        .getList()
        .then((response) => {
          this.supplierItems = response.result;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    refreshSubCompany() {
      this.subCompanyApi
        .getList()
        .then((response) => {
          this.subCompanyItems = response.result;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onSubmit() {
      this.submitting = true;

      if (this.selectedSubCompany) {
        this.obj.subCompanyId = this.selectedSubCompany.id;
      }
      if (this.selectedSupplier) {
        this.obj.supplierId = this.selectedSupplier.id;
      }

      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Purchase confirmed successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/ConfirmBuyList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Purchase updated successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/ConfirmBuyList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.selectedSubCompany = this.obj.subCompany;
            this.selectedSupplier = this.obj.supplier;
            this.loading = false;
            this.submitting = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
            this.submitting = false;
          });
      } else {
        this.loading = false;
        this.submitting = false;
      }
    },

    cancel() {
      this.$router.push({ path: "/admins/ConfirmBuyList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.confirm-buy-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Image Container */
.image-container {
  position: relative;
  width: 100%;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
}

.image-container img {
  border-radius: 8px;
}

/* Button Overlay */
.button-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-container:hover .button-overlay {
  opacity: 1;
}

.button-row {
  display: flex;
  gap: 5px;
}

.move-button {
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.875rem;
}

/* Default Badge */
.default-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #ffc107;
  color: #000;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Maximized Image */
.maximized-image {
  width: 100%;
  height: auto;
  max-width: 100%;
  border-radius: 8px;
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .confirm-buy-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }

  .image-container {
    max-width: 150px;
  }

  .move-button {
    width: 35px;
    height: 35px;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer,
  .button-overlay {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
